// dashboard/src/pages/auth/LoginPage.tsx

import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { Eye, EyeOff, Loader2, CheckCircle } from 'lucide-react'
import { isCognitoConfigured } from '@/config/aws'

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  )
}

export default function LoginPage() {
  const location = useLocation()
  const [email, setEmail] = useState(location.state?.email || '')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showVerifiedBanner, setShowVerifiedBanner] = useState(!!location.state?.verified)
  const { login, signInWithGoogle, googleOAuthAvailable, isLoading, isAuthenticated, error, clearError } = useAuth()
  const navigate = useNavigate()

  const isDemo = !isCognitoConfigured()

  // Detect OAuth callback (URL has ?code= param) — Amplify is exchanging the code for tokens
  const isOAuthCallback = window.location.search.includes('code=')

  // Redirect to dashboard once authenticated (handles OAuth redirect completion)
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, navigate])

  // Clear any errors when coming from verification and hide banner after 5 seconds
  useEffect(() => {
    if (location.state?.verified) {
      clearError() // Clear any lingering errors from verification flow
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Hide verified banner after 5 seconds
  useEffect(() => {
    if (showVerifiedBanner) {
      const timer = setTimeout(() => setShowVerifiedBanner(false), 5000)
      return () => clearTimeout(timer)
    }
  }, [showVerifiedBanner])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()
    
    try {
      await login(email, password)
      navigate('/dashboard')
    } catch (err: any) {
      if (err.message === 'CONFIRM_SIGN_UP') {
        navigate('/verify', { state: { email } })
      }
    }
  }

  // Show loading spinner while Amplify processes the OAuth authorization code
  if (isOAuthCallback || (isLoading && !email)) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8 animate-fade-in flex flex-col items-center justify-center min-h-[300px]">
        <Loader2 className="w-10 h-10 animate-spin text-yellow-500 mb-4" />
        <p className="text-gray-600 font-medium">Completing sign in...</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 animate-fade-in">
      {/* Verified success banner */}
      {showVerifiedBanner && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
          <div>
            <p className="text-sm text-green-800 font-medium">Email verified successfully!</p>
            <p className="text-xs text-green-600 mt-1">You can now sign in to your account.</p>
          </div>
        </div>
      )}
      {/* Demo mode banner */}
      {isDemo && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800 font-medium">🎭 Demo Mode</p>
          <p className="text-xs text-yellow-600 mt-1">
            Use: demo@snatched.store / Demo123!
          </p>
        </div>
      )}

      {/* Mobile logo */}
      <div className="lg:hidden text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-500 rounded-2xl mb-4">
          <span className="text-3xl">💬</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">ReplyForge</h1>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Welcome back</h2>
        <p className="text-gray-600 mt-1">Sign in to your account</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
          {error.includes('register first') && (
            <Link to="/register" className="text-sm text-yellow-600 hover:text-yellow-700 font-medium mt-2 inline-block">
              Create an account
            </Link>
          )}
        </div>
      )}

      {/* Google Sign In */}
      {googleOAuthAvailable && (
        <>
          <button
            type="button"
            onClick={signInWithGoogle}
            disabled={isLoading}
            className="w-full py-3 px-4 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <GoogleIcon />
            <span className="font-medium text-gray-700">Continue with Google</span>
          </button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">or</span>
            </div>
          </div>
        </>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors"
            placeholder="name@company.com"
            required
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors pr-12"
              placeholder="••••••••"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center">
            <input
              type="checkbox"
              className="w-4 h-4 border-gray-300 rounded text-yellow-500 focus:ring-yellow-500"
            />
            <span className="ml-2 text-sm text-gray-600">Remember me</span>
          </label>
          <Link to="/forgot-password" className="text-sm text-yellow-600 hover:text-yellow-700 font-medium">
            Forgot password?
          </Link>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 px-4 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign in'
          )}
        </button>
      </form>

      <div className="mt-8 text-center">
        <p className="text-gray-600">
          Don't have an account?{' '}
          <Link to="/register" className="text-yellow-600 hover:text-yellow-700 font-medium">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  )
}
